import { capitalize } from "lodash";
import PATHS from "routes/paths";

export const ANIMATION_DURATION = 1000;
export const CHARACTER_DELAY = 40; // 40ms between characters

/**
 * Calculates slide distance based on element and canvas bounds
 * @param {Object} elementBounds - Element bounding rectangle
 * @param {Object} canvasBounds - Canvas bounding rectangle
 * @param {string} direction - Slide direction
 * @param {number} zoom - Current zoom level
 * @returns {number} Slide distance in pixels
 */
const calculateSlideDistance = (
  elementBounds,
  canvasBounds,
  direction,
  zoom
) => {
  let distance = 0;

  if (direction === "left") {
    distance =
      (elementBounds.left - canvasBounds.left + elementBounds.width) / zoom;
  } else if (direction === "right") {
    distance =
      (canvasBounds.right - elementBounds.right + elementBounds.width) / zoom;
  } else if (direction === "up") {
    distance =
      (elementBounds.top - canvasBounds.top + elementBounds.height) / zoom;
  } else if (direction === "down") {
    distance =
      (canvasBounds.bottom - elementBounds.bottom + elementBounds.height) /
      zoom;
  }

  // Ensure we have enough distance to slide completely off screen
  return Math.max(distance, elementBounds.height / zoom);
};

/**
 * Gets CSS transform string for slide animation
 * @param {number} distance - Distance to slide
 * @param {string} direction - Slide direction (the direction the element will move)
 * @param {boolean} isSlideOut - Whether this is a slide-out animation
 * @returns {string} CSS transform value
 */
const getSlideTransform = (distance, direction, isSlideOut = false) => {
  // For normal position (no transform)
  const noTransform = "translateX(0) translateY(0)";

  // Direction indicates where the element will move
  // For slide-in: Start from opposite side of target direction
  // For slide-out: Move in the target direction
  if (direction === "left") {
    return isSlideOut
      ? `translateX(-${distance}px)` // Move left (out)
      : `translateX(${distance}px)`; // Start from right (in)
  } else if (direction === "right") {
    return isSlideOut
      ? `translateX(${distance}px)` // Move right (out)
      : `translateX(-${distance}px)`; // Start from left (in)
  } else if (direction === "up") {
    return isSlideOut
      ? `translateY(-${distance}px)` // Move up (out)
      : `translateY(${distance}px)`; // Start from bottom (in)
  } else if (direction === "down") {
    return isSlideOut
      ? `translateY(${distance}px)` // Move down (out)
      : `translateY(-${distance}px)`; // Start from top (in)
  }
  return noTransform;
};

/**
 * Processes character-level slide animations with contextual distances
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data for bounds calculation
 * @param {number} pageId - Page ID for canvas bounds
 * @param {number} zoom - Current zoom level
 * @returns {string} Processed HTML with character spans
 */
const processCharacterSlideAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom
) => {
  const { direction, transition, delay, isPreview } = animationConfig;

  // Calculate slide distance using same logic as whole element slides
  const CANVAS_PAGE_DOM = document.getElementById(`PAGE_CANVAS-${pageId}`);
  const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0
  };

  const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
  const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
    width: elementData.width || 0,
    height: elementData.height || 0
  };

  const slideDistance = calculateSlideDistance(
    ELEMENT_BOUNDS,
    CANVAS_PAGE_BOUNDS,
    direction,
    zoom
  );

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let characterIndex = 0;

  const processNode = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.length === 0) return;

      const fragment = document.createDocumentFragment();
      const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace

      words.forEach(word => {
        if (!word) return;

        if (/\s+/.test(word)) {
          // This is whitespace - preserve it exactly as is
          const textNode = document.createTextNode(word);
          fragment.appendChild(textNode);
          return;
        }

        // Create a word wrapper to maintain word boundaries
        const wordWrapper = document.createElement("span");
        wordWrapper.style.display = "inline-block";
        wordWrapper.style.whiteSpace = "pre"; // Preserve whitespace within words

        // Process each character in the word
        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const span = document.createElement("span");
          span.textContent = char;

          // Create unique class for this character
          const charClass = `char-slide-${animationId}-${characterIndex}`;
          span.className = charClass;

          // Apply inherited styles first
          if (inheritedStyles.style) {
            span.setAttribute("style", inheritedStyles.style);
          }

          // Set initial styles for slide animation
          const existingStyle = span.getAttribute("style") || "";
          const initialStyles = [
            "display: inline-block",
            "position: relative", // Enable z-index stacking
            "vertical-align: baseline", // Maintain text baseline
            "white-space: pre" // Preserve whitespace
          ];

          // Set initial transform based on transition type
          if (transition === "in") {
            const initialTransform = getSlideTransform(
              slideDistance,
              direction,
              false
            );
            initialStyles.push(`transform: ${initialTransform}`);
            initialStyles.push("opacity: 0");
          } else {
            initialStyles.push("transform: translateX(0) translateY(0)");
            initialStyles.push("opacity: 1");
          }

          const combinedStyle =
            existingStyle +
            (existingStyle ? "; " : "") +
            initialStyles.join("; ");
          span.setAttribute("style", combinedStyle);

          wordWrapper.appendChild(span);
          characterIndex++;
        }

        fragment.appendChild(wordWrapper);
      });

      // Replace text node with character spans
      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Preserve original element's layout properties
      const computedStyle = window.getComputedStyle(node);
      const layoutProperties = [
        "display",
        "position",
        "float",
        "clear",
        "margin",
        "padding",
        "width",
        "height",
        "text-align",
        "vertical-align",
        "line-height",
        "white-space",
        "word-spacing",
        "letter-spacing",
        "text-indent"
      ];

      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      // Add computed layout properties that aren't already in inline styles
      layoutProperties.forEach(prop => {
        if (!elementStyle.includes(prop) && computedStyle[prop] !== "") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${
            computedStyle[prop]
          }`;
        }
      });

      if (elementStyle) {
        const combinedStyle = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
        currentStyles.style = combinedStyle;
        node.setAttribute("style", elementStyle);
      }

      // Process child nodes with inherited styles
      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child, currentStyles));
    }
  };

  processNode(tempDiv);

  // Generate CSS animations for character slides
  const styleId = `char-slide-animation-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;

  let cssRules = "";

  // Create keyframes and rules for each character
  for (let i = 0; i < characterIndex; i++) {
    const charClass = `.char-slide-${animationId}-${i}`;
    const charDelay = isPreview
      ? i * CHARACTER_DELAY
      : delay + i * CHARACTER_DELAY;
    const keyframeName = `charSlide${direction}${transition}-${animationId}-${i}`;

    // Create character-specific keyframe
    if (transition === "in") {
      const fromTransform = getSlideTransform(slideDistance, direction, false);
      const toTransform = "translateX(0) translateY(0)";

      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: ${fromTransform};
            opacity: 0;
          }
          to {
            transform: ${toTransform};
            opacity: 1;
          }
        }
      `;
    } else {
      const fromTransform = "translateX(0) translateY(0)";
      const toTransform = getSlideTransform(slideDistance, direction, true);

      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: ${fromTransform};
            opacity: 1;
          }
          to {
            transform: ${toTransform};
            opacity: 0;
          }
        }
      `;
    }

    // Apply animation to character with proper timing function
    cssRules += `
      ${charClass} {
        animation: ${keyframeName} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${charDelay}ms both;
        will-change: transform, opacity;
      }
    `;
  }

  styleTag.textContent = cssRules;
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Processes HTML string to wrap each character in animated spans
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data (for slide animations)
 * @param {number} pageId - Page ID (for slide animations)
 * @param {number} zoom - Zoom level (for slide animations)
 * @returns {string} Processed HTML with character spans
 */
const processCharactersForAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom
) => {
  const { name } = animationConfig;

  if (name === "slide") {
    return processCharacterSlideAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom
    );
  }

  const { direction, transition, delay, isPreview } = animationConfig;

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let characterIndex = 0;

  const processNode = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.length === 0) return;

      const fragment = document.createDocumentFragment();
      const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace

      words.forEach(word => {
        if (!word) return;

        if (/\s+/.test(word)) {
          // This is whitespace - preserve it exactly as is
          const textNode = document.createTextNode(word);
          fragment.appendChild(textNode);
          return;
        }

        // Create a word wrapper that allows proper text wrapping
        const wordWrapper = document.createElement("span");
        wordWrapper.style.cssText = `
          display: inline-block;
          white-space: nowrap;
          vertical-align: baseline;
          word-break: break-word;
        `
          .replace(/\s+/g, " ")
          .trim();

        // Process each character in the word
        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const span = document.createElement("span");
          span.textContent = char;

          const charDelay = isPreview
            ? characterIndex * CHARACTER_DELAY
            : delay + characterIndex * CHARACTER_DELAY;

          // Create unique class for this character
          const charClass = `char-${animationId}-${characterIndex}`;
          span.className = charClass;

          // Add animate.css classes
          const animateClass = getAnimateCssClass(
            name,
            direction,
            transition,
            true
          );
          span.classList.add("animate__animated", animateClass);

          // Apply inherited styles first
          if (inheritedStyles.style) {
            span.setAttribute("style", inheritedStyles.style);
          }

          // Set animation properties via inline style (append to existing styles)
          const existingStyle = span.getAttribute("style") || "";
          const animationStyles = [
            `animation-delay: ${charDelay}ms`,
            `animation-duration: ${ANIMATION_DURATION}ms`,
            `animation-fill-mode: both`,
            `display: inline-block`, // inline-block needed for animations
            `vertical-align: baseline`
          ];

          const combinedStyle =
            existingStyle +
            (existingStyle ? "; " : "") +
            animationStyles.join("; ");
          span.setAttribute("style", combinedStyle);

          wordWrapper.appendChild(span);
          characterIndex++;
        }

        fragment.appendChild(wordWrapper);
      });

      // Replace text node with character spans
      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Collect styles from this element to pass to children
      const currentStyles = { ...inheritedStyles };

      // Get inline styles from this element
      let elementStyle = node.getAttribute("style") || "";
      if (elementStyle) {
        // Combine inherited styles with current element styles
        const combinedStyle = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
        currentStyles.style = combinedStyle;
      }

      // Process child nodes with inherited styles
      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child, currentStyles));
    }
  };

  processNode(tempDiv);
  return tempDiv.innerHTML;
};

/**
 * Processes word-level animations with style preservation
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data for bounds calculation
 * @param {number} pageId - Page ID for canvas bounds
 * @param {number} zoom - Current zoom level
 * @returns {string} Processed HTML with word spans
 */
const processWordAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom
) => {
  const { name, direction, transition, delay, isPreview } = animationConfig;

  // For slide animations, use custom slide processing
  if (name === "slide") {
    return processWordSlideAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom
    );
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let wordIndex = 0;
  const wordNodes = new Map();

  const processWordNodes = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text) return;

      const words = text.match(/\S+|\s+/g) || [];
      words.forEach(word => {
        if (!word) return;

        if (/^\s+$/.test(word)) {
          const textNode = document.createTextNode(word);
          node.parentNode.insertBefore(textNode, node);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.className = `word-${animationId}-${wordIndex}`;
        wordWrapper.style.cssText = inheritedStyles.style || "";
        wordWrapper.style.display = "inline-block";
        wordWrapper.style.whiteSpace = "inherit";

        // Add animate.css classes
        const animateClass = getAnimateCssClass(
          name,
          direction,
          transition,
          true
        );
        wordWrapper.classList.add("animate__animated", animateClass);

        // Set animation properties via inline style
        const wordDelay = isPreview
          ? wordIndex * CHARACTER_DELAY
          : delay + wordIndex * CHARACTER_DELAY;

        const animationStyles = [
          `animation-delay: ${wordDelay}ms`,
          `animation-duration: ${ANIMATION_DURATION}ms`,
          `animation-fill-mode: both`
        ];

        wordWrapper.style.cssText += `; ${animationStyles.join("; ")}`;

        const textNode = document.createTextNode(word);
        wordWrapper.appendChild(textNode);

        wordNodes.set(wordWrapper, {
          index: wordIndex,
          styles: inheritedStyles
        });

        node.parentNode.insertBefore(wordWrapper, node);
        wordIndex++;
      });

      node.parentNode.removeChild(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Special handling for BR tags - preserve them exactly as is
      if (node.tagName === "BR") {
        const br = node.cloneNode(false); // Clone without children
        node.parentNode.insertBefore(br, node);
        node.parentNode.removeChild(node);
        return;
      }

      const computedStyle = window.getComputedStyle(node);
      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      const importantStyles = [
        "color",
        "font-family",
        "font-size",
        "font-weight",
        "font-style",
        "text-decoration",
        "text-decoration-line",
        "text-decoration-style",
        "text-decoration-color",
        "letter-spacing",
        "line-height",
        "text-transform",
        "vertical-align"
      ];

      importantStyles.forEach(prop => {
        const value = computedStyle[prop];
        if (value && value !== "none" && value !== "normal") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${value}`;
        }
      });

      if (elementStyle) {
        currentStyles.style = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
      }

      const prevSibling = node.previousSibling;
      const nextSibling = node.nextSibling;
      const isPartOfWord =
        node.textContent.trim().length > 0 &&
        !node.textContent.includes(" ") &&
        ((prevSibling &&
          prevSibling.nodeType === Node.ELEMENT_NODE &&
          !prevSibling.textContent.includes(" ")) ||
          (nextSibling &&
            nextSibling.nodeType === Node.ELEMENT_NODE &&
            !nextSibling.textContent.includes(" ")));

      if (isPartOfWord) {
        let wordWrapper;
        if (
          prevSibling &&
          prevSibling.className &&
          prevSibling.className.startsWith(`word-${animationId}`)
        ) {
          wordWrapper = prevSibling;
        } else {
          wordWrapper = document.createElement("span");
          wordWrapper.className = `word-${animationId}-${wordIndex} animate__animated ${getAnimateCssClass(
            name,
            direction,
            transition,
            true
          )}`;
          wordWrapper.style.cssText = currentStyles.style || "";
          wordWrapper.style.display = "inline-block";
          wordWrapper.style.whiteSpace = "inherit";

          const wordDelay = isPreview
            ? wordIndex * CHARACTER_DELAY
            : delay + wordIndex * CHARACTER_DELAY;

          const animationStyles = [
            `animation-delay: ${wordDelay}ms`,
            `animation-duration: ${ANIMATION_DURATION}ms`,
            `animation-fill-mode: both`
          ];

          wordWrapper.style.cssText += `; ${animationStyles.join("; ")}`;

          node.parentNode.insertBefore(wordWrapper, node);

          wordNodes.set(wordWrapper, {
            index: wordIndex,
            styles: currentStyles
          });
          wordIndex++;
        }

        const clonedNode = node.cloneNode(true);
        wordWrapper.appendChild(clonedNode);
        node.parentNode.removeChild(node);
        return;
      }

      const children = Array.from(node.childNodes);
      children.forEach(child => processWordNodes(child, currentStyles));

      if (node.childNodes.length === 0 && node.tagName !== "BR") {
        node.parentNode.removeChild(node);
      }
    }
  };

  processWordNodes(tempDiv);
  return tempDiv.innerHTML;
};

/**
 * Processes word-level slide animations with custom keyframes
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data for bounds calculation
 * @param {number} pageId - Page ID for canvas bounds
 * @param {number} zoom - Current zoom level
 * @returns {string} Processed HTML with word spans
 */
const processWordSlideAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom
) => {
  const { direction, transition, delay, isPreview } = animationConfig;

  const CANVAS_PAGE_DOM = document.getElementById(`PAGE_CANVAS-${pageId}`);
  const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0
  };

  const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
  const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
    width: elementData.width || 0,
    height: elementData.height || 0
  };

  const slideDistance = calculateSlideDistance(
    ELEMENT_BOUNDS,
    CANVAS_PAGE_BOUNDS,
    direction,
    zoom
  );

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let wordIndex = 0;
  const wordNodes = new Map();

  const processWordNodes = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text) return;

      const words = text.match(/\S+|\s+/g) || [];
      words.forEach(word => {
        if (!word) return;

        if (/^\s+$/.test(word)) {
          const textNode = document.createTextNode(word);
          node.parentNode.insertBefore(textNode, node);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.className = `word-${animationId}-${wordIndex}`;
        wordWrapper.style.cssText = inheritedStyles.style || "";
        wordWrapper.style.display = "inline-block";
        wordWrapper.style.whiteSpace = "inherit";
        wordWrapper.style.position = "relative";
        wordWrapper.style.verticalAlign = "baseline";

        if (transition === "in") {
          const initialTransform = getSlideTransform(
            slideDistance,
            direction,
            false
          );
          wordWrapper.style.transform = initialTransform;
          wordWrapper.style.opacity = "0";
        } else if (transition === "out") {
          wordWrapper.style.transform = "translateX(0) translateY(0)";
          wordWrapper.style.opacity = "1";
        }

        const textNode = document.createTextNode(word);
        wordWrapper.appendChild(textNode);

        wordNodes.set(wordWrapper, {
          index: wordIndex,
          styles: inheritedStyles
        });

        node.parentNode.insertBefore(wordWrapper, node);
        wordIndex++;
      });

      node.parentNode.removeChild(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Special handling for BR tags - preserve them exactly as is
      if (node.tagName === "BR") {
        const br = node.cloneNode(false);
        node.parentNode.insertBefore(br, node);
        node.parentNode.removeChild(node);
        return;
      }

      const computedStyle = window.getComputedStyle(node);
      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      const importantStyles = [
        "color",
        "font-family",
        "font-size",
        "font-weight",
        "font-style",
        "text-decoration",
        "text-decoration-line",
        "text-decoration-style",
        "text-decoration-color",
        "letter-spacing",
        "line-height",
        "text-transform",
        "vertical-align"
      ];

      importantStyles.forEach(prop => {
        const value = computedStyle[prop];
        if (value && value !== "none" && value !== "normal") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${value}`;
        }
      });

      if (elementStyle) {
        currentStyles.style = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
      }

      const prevSibling = node.previousSibling;
      const nextSibling = node.nextSibling;
      const isPartOfWord =
        node.textContent.trim().length > 0 &&
        !node.textContent.includes(" ") &&
        ((prevSibling &&
          prevSibling.nodeType === Node.ELEMENT_NODE &&
          !prevSibling.textContent.includes(" ")) ||
          (nextSibling &&
            nextSibling.nodeType === Node.ELEMENT_NODE &&
            !nextSibling.textContent.includes(" ")));

      if (isPartOfWord) {
        let wordWrapper;
        if (
          prevSibling &&
          prevSibling.className &&
          prevSibling.className.startsWith(`word-${animationId}`)
        ) {
          wordWrapper = prevSibling;
        } else {
          wordWrapper = document.createElement("span");
          wordWrapper.className = `word-${animationId}-${wordIndex}`;
          wordWrapper.style.cssText = currentStyles.style || "";
          wordWrapper.style.display = "inline-block";
          wordWrapper.style.whiteSpace = "inherit";
          wordWrapper.style.position = "relative";
          wordWrapper.style.verticalAlign = "baseline";

          if (transition === "in") {
            const initialTransform = getSlideTransform(
              slideDistance,
              direction,
              false
            );
            wordWrapper.style.transform = initialTransform;
            wordWrapper.style.opacity = "0";
          } else if (transition === "out") {
            wordWrapper.style.transform = "translateX(0) translateY(0)";
            wordWrapper.style.opacity = "1";
          }

          node.parentNode.insertBefore(wordWrapper, node);

          wordNodes.set(wordWrapper, {
            index: wordIndex,
            styles: currentStyles
          });
          wordIndex++;
        }

        const clonedNode = node.cloneNode(true);
        wordWrapper.appendChild(clonedNode);
        node.parentNode.removeChild(node);
        return;
      }

      const children = Array.from(node.childNodes);
      children.forEach(child => processWordNodes(child, currentStyles));

      if (node.childNodes.length === 0 && node.tagName !== "BR") {
        node.parentNode.removeChild(node);
      }
    }
  };

  processWordNodes(tempDiv);

  const styleId = `word-animation-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  // Don't recreate styles if we're in preview mode and they already exist
  if (existingStyle && isPreview) {
    return tempDiv.innerHTML;
  }

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;
  let cssRules = "";

  wordNodes.forEach((data, wordWrapper) => {
    const wordClass = `.word-${animationId}-${data.index}`;
    const wordDelay = isPreview
      ? data.index * CHARACTER_DELAY
      : delay + data.index * CHARACTER_DELAY;

    const keyframeName = `wordSlide${direction}${transition}-${animationId}-${data.index}`;

    if (transition === "in") {
      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: ${getSlideTransform(slideDistance, direction, false)};
            opacity: 0;
          }
          to {
            transform: translateX(0) translateY(0);
            opacity: 1;
          }
        }
      `;
    } else {
      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: translateX(0) translateY(0);
            opacity: 1;
          }
          to {
            transform: ${getSlideTransform(slideDistance, direction, true)};
            opacity: 0;
          }
        }
      `;
    }

    cssRules += `
      ${wordClass} {
        animation: ${keyframeName} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${wordDelay}ms both;
        display: inline-block;
        white-space: inherit;
        position: relative;
        z-index: ${data.index + 1};
        will-change: transform, opacity;
      }
      ${wordClass} * {
        display: inline-block;
        white-space: inherit;
      }
    `;
  });

  styleTag.textContent = cssRules;
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Creates character-level animation styles and processes displayValue
 * @param {Object} elementData - Element data containing animation config
 * @param {number} pageId - ID of the current page
 * @param {number} zoom - Current zoom level
 * @returns {Object} Animation configuration with processed HTML
 */
const createCharacterAnimationStyles = (elementData, pageId, zoom) => {
  const {
    name,
    direction,
    transition,
    delay,
    isPreview,
    textType
  } = elementData.cssAnimation;
  const animationId = `text-anim-${elementData.uniqueId}`;

  if (!elementData.displayValue) {
    return null;
  }

  // Process the displayValue based on animation type
  const processedHTML =
    textType === "word"
      ? processWordAnimation(
          elementData.displayValue,
          animationId,
          { name, direction, transition, delay, isPreview },
          elementData,
          pageId,
          zoom
        )
      : processCharactersForAnimation(
          elementData.displayValue,
          animationId,
          { name, direction, transition, delay, isPreview },
          elementData,
          pageId,
          zoom
        );

  return {
    processedDisplayValue: processedHTML,
    animationName: "none",
    animationDuration: "0ms"
  };
};

/**
 * Creates a custom slide animation for an element
 * @param {Object} elementData - Element data containing animation config
 * @param {string} animationName - Base animation name
 * @param {string} customAnimName - Custom animation name
 * @param {string} direction - Animation direction
 * @param {number} pageId - ID of the current page
 * @param {number} zoom - Current zoom level
 * @returns {Object} Animation styles object
 */
const createSlideAnimation = (elementData, pageId, zoom) => {
  const { name, direction, transition } = elementData.cssAnimation;
  const customAnimName = `custom${name}-${elementData.uniqueId}`;
  const animationStyles = {
    animationDelay: `${
      elementData.cssAnimation.isPreview ? 0 : elementData.cssAnimation.delay
    }ms`,
    animationDuration: `${ANIMATION_DURATION}ms`,
    animationName: customAnimName,
    animationIterationCount: 1,
    animationFillMode: "both"
  };

  if (transition === "in") {
    animationStyles["data-animation-in"] = "true";
    animationStyles.opacity = "0 !important";
  }

  // Before creating a new style tag, check if one with this ID already exists
  const customAnimationId = `custom-animation-${customAnimName}`;
  const existingStyleTag = document.getElementById(customAnimationId);
  let styleTag;

  // Don't recreate styles if we're in preview mode and they already exist
  if (existingStyleTag && elementData.cssAnimation.isPreview) {
    return animationStyles;
  }

  if (existingStyleTag) {
    styleTag = existingStyleTag;
  } else {
    styleTag = document.createElement("style");
    styleTag.id = customAnimationId;
  }

  let keyframeDefinition = "";

  const CANVAS_PAGE_DOM = document.getElementById(`PAGE_CANVAS-${pageId}`);
  const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0
  };
  const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
  const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
    width: elementData.width || 0,
    height: elementData.height || 0
  };

  // Use shared utility to calculate slide distance
  const slideDistance = calculateSlideDistance(
    ELEMENT_BOUNDS,
    CANVAS_PAGE_BOUNDS,
    direction,
    zoom
  );

  // Define animations based on transition type (in or out)
  if (transition === "in") {
    keyframeDefinition = `
      @keyframes ${customAnimName} {
        from {
          transform: ${getSlideTransform(slideDistance, direction, false)};
          opacity: 0;
        }
        to {
          transform: translateX(0) translateY(0);
          opacity: 1;
        }
      }
    `;
  } else if (transition === "out") {
    keyframeDefinition = `
      @keyframes ${customAnimName} {
        from {
          transform: translateX(0) translateY(0);
          opacity: 1;
        }
        to {
          transform: ${getSlideTransform(slideDistance, direction, true)};
          opacity: 0;
        }
      }
    `;

    // For slide-out, ensure the element stays hidden after animation
    animationStyles.opacity = "0";
  }

  styleTag.textContent = keyframeDefinition;
  document.head.appendChild(styleTag);

  // Add an animation end listener to remove the style tag when animation completes
  const element = document.getElementById(elementData.uniqueId);
  if (element) {
    const onAnimationEnd = () => {
      const styleElement = document.getElementById(customAnimationId);
      if (styleElement) {
        styleElement.remove();
      }
      element.removeEventListener("animationend", onAnimationEnd);
    };

    element.addEventListener("animationend", onAnimationEnd);
  }

  return animationStyles;
};

/**
 * Maps animation names to animate.css classes
 * @param {string} animationName - Animation name
 * @param {string} animationDirection - Animation direction
 * @param {string} animationTransition - Animation transition (in/out)
 * @returns {string} Animate.css class name
 */
const getAnimateCssClass = (
  animationName,
  animationDirection,
  animationTransition,
  isTextboxAnimation = false
) => {
  // Map our animation names to animate.css equivalents
  let name = animationName;
  if (animationName === "boing") {
    name = "bounce";
  }

  let direction = animationDirection;
  if (animationDirection === "horizontal") {
    direction = "x";
  } else if (animationDirection === "vertical") {
    direction = "y";
  } else if (animationDirection === "center") {
    direction = "";
    // library directions should be flipped for slide-in animations
  } else if (animationDirection === "left" && animationTransition === "in") {
    direction = "right";
  } else if (animationDirection === "right" && animationTransition === "in") {
    direction = "left";
  }

  return `${isTextboxAnimation ? "animate__" : ""}${name}${capitalize(
    animationTransition
  )}${capitalize(direction)}`;
};

/**
 * Converts cssAnimation object to inline style properties
 * @param {Object} cssAnimation - Animation configuration object
 * @param {number} cssAnimation.delay - Animation delay in milliseconds
 * @param {number} cssAnimation.duration - Animation duration in milliseconds
 * @param {string} cssAnimation.direction - Animation name/direction
 * @param {string} cssAnimation.transition - Animation transition
 * @param {boolean} isPlaying - Whether the animation is playing
 * @returns {Object|null} Animation style object or null if no animation
 */
export const getAnimationStyles = (elementData, isPlaying, pageId, zoom) => {
  if (
    !(
      elementData?.cssAnimation &&
      (isPlaying || elementData.cssAnimation.isPreview)
    )
  ) {
    return null;
  }

  // Only clean up existing animation styles if we're not currently in preview mode
  // This prevents animation flickering during mouse movements while preview is active
  const animationId = `text-anim-${elementData.uniqueId}`;
  const charStyleId = `char-slide-animation-${animationId}`;
  const wordStyleId = `word-animation-${animationId}`;
  const customAnimName = `custom${elementData.cssAnimation.name}-${elementData.uniqueId}`;
  const customAnimationId = `custom-animation-${customAnimName}`;

  // Check if we're in preview mode and styles already exist
  const isInPreviewMode = elementData.cssAnimation.isPreview;
  const hasExistingStyles =
    document.getElementById(charStyleId) ||
    document.getElementById(wordStyleId) ||
    document.getElementById(customAnimationId);

  // Only clean up if not in preview mode OR if no existing styles are found
  if (!isInPreviewMode || !hasExistingStyles) {
    [charStyleId, wordStyleId, customAnimationId].forEach(styleId => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    });
  }

  // Check for text-level animations (both character and word)
  if (
    elementData.cssAnimation.textType === "letter" ||
    elementData.cssAnimation.textType === "word"
  ) {
    return createCharacterAnimationStyles(elementData, pageId, zoom);
  }

  const animationName = getAnimateCssClass(
    elementData.cssAnimation.name,
    elementData.cssAnimation.direction,
    elementData.cssAnimation.transition
  );

  const animationStyles = {
    animationDelay: `${
      elementData.cssAnimation.isPreview ? 0 : elementData.cssAnimation.delay
    }ms`,
    animationDuration: `${ANIMATION_DURATION}ms`,
    animationName: animationName,
    animationIterationCount: 1,
    animationFillMode: "both"
  };

  // For slide animations, we need to modify how they're applied
  if (
    (isPlaying || elementData.cssAnimation.isPreview) &&
    elementData.cssAnimation.name === "slide"
  ) {
    return createSlideAnimation(elementData, pageId, zoom);
  }

  if (PATHS.isRenderer(window.location.pathname)) {
    animationStyles.animationPlayState = "paused";
  }

  return animationStyles;
};

/**
 * Combines element style with animation styles if present
 * @param {Object} elementStyle - Base element styles
 * @param {Object} elementData - Element data containing animation config
 * @param {boolean} isPlaying - Whether the animation is playing
 * @returns {Object} Combined style object
 */
export const getCombinedAnimatedStyles = (
  elementStyle,
  elementData,
  isPlaying,
  pageId,
  zoom
) => {
  const animationStyles = getAnimationStyles(
    elementData,
    isPlaying,
    pageId,
    zoom
  );

  if (!animationStyles) {
    return elementStyle;
  }

  return {
    ...elementStyle,
    ...animationStyles
  };
};

export const shouldAnimateElement = (elementData, isPlaying) => {
  if (!elementData.cssAnimation) {
    return false;
  }

  return Boolean(isPlaying || elementData.cssAnimation?.isPreview);
};

/**
 * Calculates the total duration of an animation for all elements
 * @param {Array|Object} elements - Single element or array of elements being animated
 * @returns {number} Total animation duration in milliseconds
 */
export const calculateTotalAnimationDuration = elements => {
  // Ensure elements is an array
  const elementArray = Array.isArray(elements) ? elements : [elements];

  let maxDuration = 0;

  elementArray.forEach(element => {
    let elementDuration = ANIMATION_DURATION; // Base duration (1000ms) for all elements

    // For textbox elements, calculate additional duration based on their individual text type
    if (element?.type === "textbox" && element?.cssAnimation?.textType) {
      const textType = element.cssAnimation.textType;

      // If textType is "textbox", just use base duration
      if (textType === "textbox") {
        elementDuration = ANIMATION_DURATION;
      } else if (textType === "letter" || textType === "word") {
        // Calculate character count for letter/word animations
        const displayValue = element.displayValue || "";

        const charCount = displayValue.replace(/<[^>]*>/g, "").length; // Strip HTML tags but keep spaces for counting

        if (charCount > 0) {
          const delayPerChar =
            textType === "word"
              ? Math.ceil(charCount / 5) * CHARACTER_DELAY // Approximate words by dividing chars by 5
              : charCount * CHARACTER_DELAY; // Letter animations

          elementDuration += delayPerChar;
        }
      }
    }

    maxDuration = Math.max(maxDuration, elementDuration);
  });

  return maxDuration;
};
